import { SCORING_RULE, ScoringRuleType } from '../../../../../constants/scoringRule';
import { ScoringRuleItemType } from '../../../../../interfaces/leaderboard/ScoringRuleItemType';

const rulesIntroVideoUrls = {
  [SCORING_RULE.FAT_LOSS_MUSCLE_GAIN_FOOD_TRACKING]:
    'https://www.youtube.com/watch?v=0_qL_5MqEw0',
  [SCORING_RULE.FAT_LOSS_MUSCLE_GAIN]: 'https://www.youtube.com/watch?v=A4hrmC9OQdc',
  [SCORING_RULE.FAT_LOSS]: 'https://www.youtube.com/watch?v=NGb2TNLjAbU',
};

export const scoringRulesContent: Record<ScoringRuleType, ScoringRuleItemType[]> = {
  [SCORING_RULE.FAT_LOSS_MUSCLE_GAIN_FOOD_TRACKING]: [
    {
      title: 'How You Are Ranked',
      content: `
        <p>This challenge rewards body transformation and nutrition consistency.</p>
        <p>Your final score combines:</p>
        <ol>
          <li>Adjusted Fat Loss %</li>
          <li>Nutrition Compliance Ranking</li>
        </ol>
        <p>Lowest total score wins.</p>
      `,
    },
    {
      title: 'Part 1: Adjusted Fat Loss %',
      content: `
        <p><strong>Step 1: Body Fat Loss %</strong></p>
        <p>This is the percentage of your starting body fat that you lose during the challenge.</p>
        <p><strong>Example:</strong></p>
        <ul>
          <li>Start with 40 lbs of body fat → Lose 10 lbs = 25% fat loss</li>
          <li>Start with 25 lbs of body fat → Lose 5 lbs = 20% fat loss</li>
        </ul>
        <p><strong>Step 2: Muscle Multiplier</strong></p>
        <p>After your Body Fat Loss % is calculated:</p>
        <p><strong>Example:</strong></p>
        <ul>
          <li>If you gain muscle, your score increases.</li>
          <li>If you lose muscle, your score decreases.</li>
        </ul>
        <p>This creates your Adjusted Fat Loss %, which determines your final ranking.</p>
        <p>Participants are ranked from best to worst.</p>
        <ul>
          <li>1st place = 1 point</li>
          <li>10th place = 10 points</li>
        </ul>
        <p>Lower points are better.</p>
      `,
    },
    {
      title: 'Part 2: Nutrition Compliance',
      content: `
        <p>Starting one week after the challenge begins, participants track:</p>
        <ul>
          <li>Protein</li>
          <li>Carbs</li>
          <li>Fats</li>
        </ul>
        <p>Your goal is to be as close to 100% of your assigned macros as possible.</p>
        <p><strong>How Scoring Works</strong></p>
        <ul>
          <li>Protein is not penalized for going over.</li>
          <li>Carbs and fats are scored based on how close you are to 100%.</li>
        </ul>
        <p>We measure how close you are to your targets.<br/>Your protein, carb, and fat scores are averaged to create your Nutrition Percentage</p>
        <p>Participants are ranked from best to worst.</p>
        <ul>
          <li>1st place = 1 point</li>
          <li>10th place = 10 points</li>
        </ul>
      `,
    },
    {
      title: 'Final Score',
      content: `
        <p>Your final score = Recomposition Rank + Nutrition Rank</p>
        <ul>
          <li>1st in recomposition + 1st in nutrition = 2 points</li>
          <li>10th in both = 20 points</li>
        </ul>
        <p>Lowest total score wins.</p>
        <p>This challenge rewards:</p>
        <ul>
          <li>Losing fat</li>
          <li>Preserving or gaining muscle</li>
          <li>Following your nutrition plan consistently</li>
        </ul>
        <p>Not just results — but discipline.</p>
      `,
    },
    {
      content: `<h2><a href="${rulesIntroVideoUrls[SCORING_RULE.FAT_LOSS_MUSCLE_GAIN_FOOD_TRACKING]}" target="_blank">Watch video explainer</a></h2>`,
    },
  ],
  [SCORING_RULE.FAT_LOSS_MUSCLE_GAIN]: [
    {
      title: 'How You Are Ranked',
      content: `
        <p>Participants are ranked by their Adjusted Fat Loss %.<br/>Your score starts with your Body Fat Loss % and is adjusted based on muscle gained or lost.</p>
        <p>Higher adjusted percentage = Higher position on the leaderboard.</p>
      `,
    },
    {
      title: 'Step 1: Body Fat Loss %',
      content: `
        <p>This is the percentage of your starting body fat that you lose during the challenge.<br/>You are competing against your own starting point.</p>
        <p><strong>Example:</strong></p>
        <ul>
          <li>Start with 40 lbs of body fat → Lose 10 lbs = 25% fat loss</li>
          <li>Start with 25 lbs of body fat → Lose 5 lbs = 20% fat loss</li>
        </ul>
      `,
    },
    {
      title: 'Step 2: Muscle Multiplier',
      content: `
        <p>After your Body Fat Loss % is calculated:</p>
        <p><strong>Example:</strong></p>
        <ul>
          <li>If you gain muscle, your score increases.</li>
          <li>If you lose muscle, your score decreases.</li>
        </ul>
        <p>This creates your Adjusted Fat Loss %, which determines your final ranking.</p>
      `,
    },
    {
      title: 'Adjustment Fat Loss %,',
      content: `
        <p>Two participants both lose 20% of their body fat.</p>
        <ul>
          <li>Participant A gains muscle → Adjusted score increases</li>
          <li>Participant B loses muscle → Adjusted score decreases.</li>
        </ul>
      `,
      note: 'The person who improves body composition the right way moves up.',
    },
    {
      title: 'Final Score',
      content: `
        <p>Your final ranking is based on your Adjusted Fat Loss %.</p>
        <p>Higher adjusted percentage = Higher leaderboard position.</p>
      `,
    },
    {
      content: `<h2><a href="${rulesIntroVideoUrls[SCORING_RULE.FAT_LOSS_MUSCLE_GAIN]}" target="_blank">Watch video explainer</a></h2>`,
    },
  ],
  [SCORING_RULE.FAT_LOSS]: [
    {
      title: 'How You Are Ranked',
      content: `
        <p>Participants are ranked by Percentage of Body Fat Lost.</p>
        <p>Higher percentage = Higher position on the leaderboard</p>
      `,
    },
    {
      title: 'What Is Body Fat Loss %?',
      content: `
        <p>Your Body Fat Loss % is the percentage of your starting body fat that you lose during the challenge.</p>
        <p>It is not based on total weight lost.</p>
        <p><strong>Example:</strong></p>
        <ul>
          <li>Start with 40 lbs of body fat → Lose 10 lbs = 25% fat loss</li>
          <li>Start with 25 lbs of body fat → Lose 5 lbs = 20% fat loss</li>
        </ul>
        <p>This keeps the competition fair.<br/>You are competing against your own starting point.</p>
        <p>If two participants have the same Body Fat Loss %:</p>
        <ol>
          <li>Most pounds of muscle gained</li>
        </ol>
      `,
    },
    {
      title: 'Final Score',
      content: `
        <p>Your final ranking is based solely on your Body Fat Loss %.</p>
      `,
    },
    {
      content: `<h2><a href="${rulesIntroVideoUrls[SCORING_RULE.FAT_LOSS]}" target="_blank">Watch video explainer</a></h2>`,
    },
  ],
};
